import { useContext, useRef, useState } from 'react';
import {
	Box, Heading, Text, Button, VStack, HStack, Badge, Flex,
	Table, Thead, Tbody, Tr, Th, Td, Spinner, useToast, Tag,
	Tooltip, Alert, AlertIcon, Input, InputGroup, InputLeftElement,
	Stat, StatLabel, StatNumber, SimpleGrid, Icon,
} from '@chakra-ui/react';
import { FaSearch, FaMapMarkerAlt, FaTimes, FaObjectGroup, FaArrowRight } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../constants/environment';
import SessionContext from '../contexts/SessionContext';

const iconEl = (i: IconType) => <Icon as={i as unknown as React.ElementType} />;
const INTERNAL_REVIEW_PASSWORD = 'PartBay26!';
const INTERNAL_REVIEW_UNLOCK_KEY = 'supplierReconcileReviewUnlocked';

type Source = 'ccc' | 'partstrader' | 'opstrax' | 'nex';

interface RecentSupplier {
	_id: string;
	title: string;
	cccId: number[];
	opsId: number[];
	nexIds: number[];
	partstraderId: number[];
	createdAt?: string;
	topCandidates?: Array<{
		supplierId: string;
		title: string;
		score: number;
	}>;
}

interface RecentAutoMerge {
	_id: string;
	supplierId: string;
	supplierTitle?: string;
	source: Source;
	externalId: number;
	score?: number;
	context: 'onboarding' | 'ad-hoc' | 'internal-review';
	actor?: string;
	createdAt: string;
	externalName?: string;
}

interface ReviewResponse {
	recentSuppliers: RecentSupplier[];
	recentAutoMerges: RecentAutoMerge[];
	totals: { recentSuppliers: number; recentAutoMerges: number };
}

interface SupplierSearchEntry {
	_id: string;
	title: string;
	cccId: number[];
	opsId: number[];
	nexIds: number[];
	partstraderId: number[];
	partTypes?: string[];
	createdAt?: string;
}

interface SupplierSearchResponse {
	suppliers: SupplierSearchEntry[];
	total: number;
	limit: number;
	offset: number;
}

interface GooglePlaceResult {
	formattedAddress: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
	placeId?: string;
	lat?: number;
	lng?: number;
}

interface RescoreResponse {
	subject: { supplierId: string; title: string; googleLocation?: GooglePlaceResult };
	candidates: Array<{
		supplierId: string;
		title: string;
		originalScore: number;
		newScore: number;
		googleLocation?: GooglePlaceResult;
	}>;
}

type MergeManualActionType = 'top-candidate' | 'rescore-candidate' | 'search-merge';


const SOURCE_CFG: Record<Source, { label: string; color: string }> = {
	ccc: { label: 'CCC', color: 'blue' },
	partstrader: { label: 'PT', color: 'purple' },
	opstrax: { label: 'OPS', color: 'teal' },
	nex: { label: 'NEX', color: 'orange' },
};

const sourceBadge = (source: Source | string | undefined) => {
	const cfg = (source ? SOURCE_CFG[source as Source] : undefined)
		?? { label: source ? source.toUpperCase() : 'UNKNOWN', color: 'gray' };
	return <Badge colorScheme={cfg.color} fontSize="2xs" borderRadius="full">{cfg.label}</Badge>;
};

const scoreBadge = (score?: number) => {
	if (score === undefined || score === null) return null;
	const pct = Math.round(score * 100);
	const color = pct >= 90 ? 'green' : pct >= 70 ? 'yellow' : 'red';
	return <Badge colorScheme={color} fontSize="xs">{pct}%</Badge>;
};

const marketplaceTags = (s: { cccId?: number[]; opsId?: number[]; nexIds?: number[]; partstraderId?: number[] }) => {
	const tags: JSX.Element[] = [];
	for (const id of s.cccId ?? []) tags.push(<Tag key={`c-${id}`} size="sm" colorScheme="blue">CCC {id}</Tag>);
	for (const id of s.opsId ?? []) tags.push(<Tag key={`o-${id}`} size="sm" colorScheme="teal">OPS {id}</Tag>);
	for (const id of s.nexIds ?? []) tags.push(<Tag key={`n-${id}`} size="sm" colorScheme="orange">NEX {id}</Tag>);
	for (const id of s.partstraderId ?? []) tags.push(<Tag key={`p-${id}`} size="sm" colorScheme="purple">PT {id}</Tag>);
	return <HStack spacing={1} flexWrap="wrap">{tags.length === 0 ? <Text fontSize="xs" color="gray.500">none</Text> : tags}</HStack>;
};


const SupplierReconcileReview = () => {
	const sessionContext = useContext(SessionContext);
	const toast = useToast();
	const queryClient = useQueryClient();
	const headers = { Authorization: sessionContext?.sessionId ?? '' };
	const [passwordInput, setPasswordInput] = useState('');
	const [isUnlocked, setIsUnlocked] = useState<boolean>(() => {
		if (typeof window === 'undefined') return false;
		return window.sessionStorage.getItem(INTERNAL_REVIEW_UNLOCK_KEY) === '1';
	});

	const [rescoreByMerge, setRescoreByMerge] = useState<Record<string, RescoreResponse>>({});
	const [rescoringIds, setRescoringIds] = useState<Set<string>>(new Set());
	const [inlineSearchBySupplier, setInlineSearchBySupplier] = useState<Record<string, string>>({});
	const [inlineSearchLoadingBySupplier, setInlineSearchLoadingBySupplier] = useState<Record<string, boolean>>({});
	const [inlineSearchResultsBySupplier, setInlineSearchResultsBySupplier] = useState<Record<string, SupplierSearchEntry[]>>({});
	const [inlineSearchErrorBySupplier, setInlineSearchErrorBySupplier] = useState<Record<string, boolean>>({});
	const inlineSearchRequestSeq = useRef<Record<string, number>>({});

	const review = useQuery<ReviewResponse>({
		queryKey: ['supplier-review'],
		queryFn: async () => {
			const { data } = await axios.get(`${API_URL}/internal/supplier-review`, {
				headers,
				params: {
					recentLimit: 200,
					autoMergeLimit: 500,
					daysBack: 365,
				},
			});
			return data;
		},
		enabled: isUnlocked,
	});

	const runInlineSearch = async (supplierId: string, query: string) => {
		const seq = (inlineSearchRequestSeq.current[supplierId] ?? 0) + 1;
		inlineSearchRequestSeq.current[supplierId] = seq;
		setInlineSearchLoadingBySupplier(prev => ({ ...prev, [supplierId]: true }));
		setInlineSearchErrorBySupplier(prev => ({ ...prev, [supplierId]: false }));
		try {
			const { data } = await axios.get<SupplierSearchResponse>(`${API_URL}/internal/suppliers`, {
				headers,
				params: { q: query, limit: 15 },
			});
			if (inlineSearchRequestSeq.current[supplierId] !== seq) return;
			setInlineSearchResultsBySupplier(prev => ({
				...prev,
				[supplierId]: (data.suppliers ?? []).filter(s => s._id !== supplierId),
			}));
		} catch {
			if (inlineSearchRequestSeq.current[supplierId] !== seq) return;
			setInlineSearchResultsBySupplier(prev => ({ ...prev, [supplierId]: [] }));
			setInlineSearchErrorBySupplier(prev => ({ ...prev, [supplierId]: true }));
		} finally {
			if (inlineSearchRequestSeq.current[supplierId] !== seq) return;
			setInlineSearchLoadingBySupplier(prev => ({ ...prev, [supplierId]: false }));
		}
	};

	const onInlineSearchChange = (supplierId: string, value: string) => {
		setInlineSearchBySupplier(prev => ({ ...prev, [supplierId]: value }));
		const trimmed = value.trim();
		if (trimmed.length < 2) {
			setInlineSearchResultsBySupplier(prev => ({ ...prev, [supplierId]: [] }));
			setInlineSearchErrorBySupplier(prev => ({ ...prev, [supplierId]: false }));
			setInlineSearchLoadingBySupplier(prev => ({ ...prev, [supplierId]: false }));
			return;
		}
		runInlineSearch(supplierId, trimmed);
	};

	const rescore = useMutation({
		mutationFn: async (supplierId: string) => {
			setRescoringIds(prev => new Set(prev).add(supplierId));
			try {
				const { data } = await axios.post<RescoreResponse>(
					`${API_URL}/internal/supplier-review/rescore`,
					{ supplierId, topN: 5 },
					{ headers },
				);
				return { supplierId, data };
			} finally {
				setRescoringIds(prev => {
					const next = new Set(prev);
					next.delete(supplierId);
					return next;
				});
			}
		},
		onSuccess: ({ supplierId, data }) => {
			setRescoreByMerge(prev => ({ ...prev, [supplierId]: data }));
			toast({ title: 'Rescore complete', status: 'success', duration: 2500 });
		},
		onError: () => toast({ title: 'Rescore failed', status: 'error', duration: 3000 }),
	});

	const rejectAutoMerge = useMutation({
		mutationFn: async ({ supplierId, source, externalId }: { supplierId: string; source: Source; externalId: number }) => {
			const { data } = await axios.post(`${API_URL}/internal/supplier-review/action`, {
				action: 'reject-auto-merge', supplierId, source, externalId,
			}, { headers });
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['supplier-review'] });
			queryClient.invalidateQueries({ queryKey: ['supplier-search'] });
			toast({ title: 'Auto-merge rejected', status: 'success', duration: 2500 });
		},
		onError: () => toast({ title: 'Reject failed', status: 'error', duration: 3000 }),
	});

	const mergeSuppliers = useMutation({
		mutationFn: async ({
			sourceSupplierId,
			targetSupplierId,
			manualActionType,
		}: {
			sourceSupplierId: string;
			targetSupplierId: string;
			manualActionType?: MergeManualActionType;
		}) => {
			const { data } = await axios.post(`${API_URL}/internal/supplier-review/action`, {
				action: 'merge-suppliers',
				sourceSupplierId,
				targetSupplierId,
				manualActionType,
			}, { headers });
			return data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['supplier-review'] });
			queryClient.invalidateQueries({ queryKey: ['supplier-search'] });
			if (variables.manualActionType === 'search-merge') {
				setInlineSearchBySupplier(prev => ({ ...prev, [variables.sourceSupplierId]: '' }));
				setInlineSearchResultsBySupplier(prev => ({ ...prev, [variables.sourceSupplierId]: [] }));
			}
			toast({
				title: 'Suppliers merged',
				description: 'Manual action posted to Slack.',
				status: 'success',
				duration: 2500,
			});
		},
		onError: () => toast({ title: 'Merge failed', status: 'error', duration: 3000 }),
	});

	const unlockInternalReview = () => {
		if (passwordInput === INTERNAL_REVIEW_PASSWORD) {
			setIsUnlocked(true);
			if (typeof window !== 'undefined') {
				window.sessionStorage.setItem(INTERNAL_REVIEW_UNLOCK_KEY, '1');
			}
			setPasswordInput('');
			return;
		}
		toast({ title: 'Incorrect password', status: 'error', duration: 2500 });
	};

	if (!isUnlocked) {
		return (
			<Flex minH="70vh" align="center" justify="center" p={8}>
				<Box borderWidth={1} borderRadius="md" p={6} w="100%" maxW="420px">
					<Heading size="md" mb={2}>Internal Access Required</Heading>
					<Text fontSize="sm" color="gray.600" mb={4}>
						Enter the internal password to view Supplier Reconciliation Review.
					</Text>
					<Input
						type="password"
						placeholder="Password"
						value={passwordInput}
						onChange={(e) => setPasswordInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') unlockInternalReview();
						}}
						mb={3}
					/>
					<Button colorScheme="blue" width="100%" onClick={unlockInternalReview}>
						Unlock
					</Button>
				</Box>
			</Flex>
		);
	}

	if (review.isLoading) {
		return (
			<Flex align="center" justify="center" p={8}>
				<Spinner size="lg" />
			</Flex>
		);
	}

	if (review.isError || !review.data) {
		return (
			<Box p={8}>
				<Alert status="error"><AlertIcon />Failed to load review data.</Alert>
			</Box>
		);
	}

	const { recentSuppliers, recentAutoMerges, totals } = review.data;

	return (
		<Box p={8}>
			<Heading size="lg" mb={2}>Supplier Reconciliation Review</Heading>
			<Text color="gray.600" mb={6}>
				Canonical suppliers are the source of truth. This page surfaces recently
				created suppliers and recently auto-merged external IDs for auditing.
			</Text>

			<SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
				<Stat p={4} borderWidth={1} borderRadius="md">
					<StatLabel>Recent suppliers</StatLabel>
					<StatNumber>{totals.recentSuppliers}</StatNumber>
				</Stat>
				<Stat p={4} borderWidth={1} borderRadius="md">
					<StatLabel>Recent auto-merges</StatLabel>
					<StatNumber>{totals.recentAutoMerges}</StatNumber>
				</Stat>
				<Stat p={4} borderWidth={1} borderRadius="md">
					<StatLabel>Actions</StatLabel>
					<StatNumber fontSize="sm"><Button size="sm" onClick={() => review.refetch()}>Refresh</Button></StatNumber>
				</Stat>
			</SimpleGrid>

			<Box mb={8}>
				<Heading size="md" mb={3}>Recent Auto-Merges</Heading>
				{recentAutoMerges.length === 0 ? (
					<Alert status="info"><AlertIcon />No auto-merges in the selected window.</Alert>
				) : (
					<Box maxH="55vh" overflowY="auto" pr={2}>
						<Table variant="simple" size="sm">
							<Thead>
								<Tr>
									<Th>Source</Th>
									<Th>External ID</Th>
									<Th>External Name</Th>
									<Th>Supplier</Th>
									<Th>Score</Th>
									<Th>Context</Th>
									<Th>When</Th>
									<Th>Action</Th>
								</Tr>
							</Thead>
							<Tbody>
								{recentAutoMerges.map(am => (
									<Tr key={am._id}>
										<Td>{sourceBadge(am.source)}</Td>
										<Td fontFamily="mono" fontSize="xs">{am.externalId}</Td>
										<Td>
											<Tooltip label={am.externalName ?? ''}>
												<Text maxW="250px" isTruncated>{am.externalName ?? '—'}</Text>
											</Tooltip>
										</Td>
										<Td>
											<Tooltip label={am.supplierTitle ?? ''}>
												<Text maxW="250px" isTruncated>{am.supplierTitle ?? am.supplierId}</Text>
											</Tooltip>
										</Td>
										<Td>{scoreBadge(am.score)}</Td>
										<Td><Text fontSize="xs" color="gray.600">{am.context}</Text></Td>
										<Td><Text fontSize="xs" color="gray.600">{new Date(am.createdAt).toLocaleString()}</Text></Td>
										<Td>
											<Button
												size="xs"
												colorScheme="red"
												leftIcon={iconEl(FaTimes)}
												isLoading={rejectAutoMerge.isPending && rejectAutoMerge.variables?.supplierId === am.supplierId && rejectAutoMerge.variables?.externalId === am.externalId}
												onClick={() => rejectAutoMerge.mutate({ supplierId: am.supplierId, source: am.source, externalId: am.externalId })}
											>
												Reject
											</Button>
										</Td>
									</Tr>
								))}
							</Tbody>
						</Table>
					</Box>
				)}
			</Box>

			<Box mb={8}>
				<Heading size="md" mb={3}>Recently Created Suppliers</Heading>
				{recentSuppliers.length === 0 ? (
					<Alert status="info"><AlertIcon />No recently created suppliers in the window.</Alert>
				) : (
					<Box maxH="75vh" overflowY="auto" pr={2}>
						<VStack align="stretch" spacing={3}>
							{recentSuppliers.map(s => {
							const rescored = rescoreByMerge[s._id];
							const isRescoring = rescoringIds.has(s._id);
							const topCandidates = (s.topCandidates ?? []).filter(c => c.supplierId !== s._id).slice(0, 3);
							const inlineSearchQ = inlineSearchBySupplier[s._id] ?? '';
							const inlineSearchResults = inlineSearchResultsBySupplier[s._id] ?? [];
							const inlineSearchLoading = inlineSearchLoadingBySupplier[s._id] ?? false;
							const inlineSearchError = inlineSearchErrorBySupplier[s._id] ?? false;
							return (
								<Box key={s._id} borderWidth={1} borderRadius="md" p={4}>
									<Flex justify="space-between" align="start" mb={2} gap={4}>
										<Box>
											<Text fontWeight="bold">{s.title}</Text>
											<Text fontSize="xs" color="gray.500" fontFamily="mono">{s._id}</Text>
										</Box>
										<Text fontSize="xs" color="gray.600">
											{s.createdAt ? new Date(s.createdAt).toLocaleString() : '—'}
										</Text>
									</Flex>
									<Box mb={3}>{marketplaceTags(s)}</Box>
									<HStack>
										<Button
											size="sm"
											leftIcon={iconEl(FaMapMarkerAlt)}
											onClick={() => rescore.mutate(s._id)}
											isLoading={isRescoring}
										>
											Run Google Rescore
										</Button>
									</HStack>
									<Box mt={3} p={3} bg="gray.50" borderRadius="md">
										<Text fontSize="sm" fontWeight="semibold" mb={2}>
											Top 3 initial candidates
										</Text>
										<VStack align="stretch" spacing={2}>
											{topCandidates.map(c => (
												<Flex key={c.supplierId} justify="space-between" align="center" p={2} bg="white" borderRadius="md" borderWidth={1}>
													<Box>
														<Text fontSize="sm" fontWeight="medium">{c.title}</Text>
														<Text fontSize="xs" color="gray.600" fontFamily="mono">{c.supplierId}</Text>
													</Box>
													<HStack>
														{scoreBadge(c.score)}
														<Button
															size="xs"
															leftIcon={iconEl(FaArrowRight)}
															onClick={() => mergeSuppliers.mutate({
																sourceSupplierId: s._id,
																targetSupplierId: c.supplierId,
																manualActionType: 'top-candidate',
															})}
														>
															Merge into this
														</Button>
													</HStack>
												</Flex>
											))}
											{topCandidates.length === 0 && (
												<Text fontSize="xs" color="gray.500">No initial candidates were available.</Text>
											)}
										</VStack>
									</Box>
									{rescored && (
										<Box mt={3} p={3} bg="gray.50" borderRadius="md">
											<Text fontSize="sm" fontWeight="semibold" mb={2}>
												Google rescored candidates
											</Text>
											<Text fontSize="xs" color="gray.600" mb={2}>
												Subject: {rescored.subject.googleLocation?.formattedAddress ?? '(no Google match)'}
											</Text>
											<VStack align="stretch" spacing={2}>
												{rescored.candidates.map(c => (
													<Flex key={c.supplierId} justify="space-between" align="center" p={2} bg="white" borderRadius="md" borderWidth={1}>
														<Box>
															<Text fontSize="sm" fontWeight="medium">{c.title}</Text>
															<Text fontSize="xs" color="gray.600">{c.googleLocation?.formattedAddress ?? ''}</Text>
														</Box>
														<HStack>
															<Tooltip label={`Original: ${Math.round(c.originalScore * 100)}%`}>{scoreBadge(c.newScore)}</Tooltip>
															<Button
																size="xs"
																leftIcon={iconEl(FaArrowRight)}
																onClick={() => mergeSuppliers.mutate({
																	sourceSupplierId: s._id,
																	targetSupplierId: c.supplierId,
																	manualActionType: 'rescore-candidate',
																})}
															>
																Merge into this
															</Button>
														</HStack>
													</Flex>
												))}
												{rescored.candidates.length === 0 && (
													<Text fontSize="xs" color="gray.500">No nearby candidates found.</Text>
												)}
											</VStack>
										</Box>
									)}
									<Box mt={3} p={3} borderWidth={1} borderRadius="md">
										<Text fontSize="sm" fontWeight="semibold" mb={2}>Search and merge manually</Text>
										<InputGroup size="sm" mb={2}>
											<InputLeftElement pointerEvents="none">
												<Icon as={FaSearch as unknown as React.ElementType} color="gray.400" />
											</InputLeftElement>
											<Input
												placeholder="Search existing suppliers..."
												value={inlineSearchQ}
												onChange={e => onInlineSearchChange(s._id, e.target.value)}
											/>
										</InputGroup>
										{inlineSearchQ.trim().length < 2 ? (
											<Text fontSize="xs" color="gray.500">Type at least 2 characters to search.</Text>
										) : inlineSearchLoading ? (
											<Flex justify="center" p={2}><Spinner size="sm" /></Flex>
										) : inlineSearchError ? (
											<Alert status="error" borderRadius="md">
												<AlertIcon />
												Search failed. Try again.
											</Alert>
										) : (
											<VStack align="stretch" spacing={2}>
												{inlineSearchResults.map(candidate => (
													<Flex key={candidate._id} justify="space-between" align="center" p={2} borderWidth={1} borderRadius="md">
														<Box>
															<Text fontSize="sm" fontWeight="medium">{candidate.title}</Text>
															<Text fontSize="xs" color="gray.600" fontFamily="mono">{candidate._id}</Text>
														</Box>
														<Button
															size="xs"
															leftIcon={iconEl(FaArrowRight)}
															isLoading={
																mergeSuppliers.isPending &&
																mergeSuppliers.variables?.sourceSupplierId === s._id &&
																mergeSuppliers.variables?.targetSupplierId === candidate._id
															}
															onClick={() => mergeSuppliers.mutate({
																sourceSupplierId: s._id,
																targetSupplierId: candidate._id,
																manualActionType: 'search-merge',
															})}
														>
															Merge into this
														</Button>
													</Flex>
												))}
												{inlineSearchResults.length === 0 && (
													<Text fontSize="xs" color="gray.500">No suppliers found for this search.</Text>
												)}
											</VStack>
										)}
									</Box>
								</Box>
							);
							})}
						</VStack>
					</Box>
				)}
			</Box>

			<Box mt={8}>
				<HStack fontSize="xs" color="gray.500">
					<Icon as={FaObjectGroup as unknown as React.ElementType} />
					<Text>Merge/move actions write to supplierAudit (internal-review context).</Text>
				</HStack>
			</Box>
		</Box>
	);
};

export default SupplierReconcileReview;
